/**
 * Notification Panel Component
 *
 * A dropdown panel displaying a list of notifications with actions.
 * Shows in the notification bell dropdown menu.
 *
 * @usage
 * <ng-notification-panel (closePanel)="onClose()"></ng-notification-panel>
 */

import { Component, EventEmitter, Output, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil, switchMap, of } from 'rxjs';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { NotificationService, NotificationState } from '../../../shared/services/notification.service';
import { InvitationService } from '../../../shared/services/invitation.service';
import { NotificationItemComponent } from '../notification-item/notification-item.component';
import { InAppNotification, InvitationDocument } from '@newgenus/common';

@Component({
    selector: 'newgenus-notification-panel',
    imports: [
        CommonModule,
        MatButtonModule,
        MatDividerModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        NotificationItemComponent,
    ],
    template: `
        <div class="notification-panel" (click)="$event.stopPropagation()">
            <!-- Header -->
            <div class="panel-header">
                <h3>Notifications</h3>
                <div class="header-actions">
                    <button
                        mat-icon-button
                        matTooltip="Mark all as read"
                        (click)="markAllRead()"
                        [disabled]="state.unreadCount === 0"
                    >
                        <mat-icon>done_all</mat-icon>
                    </button>
                    <button
                        mat-icon-button
                        matTooltip="Dismiss all"
                        (click)="dismissAll()"
                        [disabled]="state.notifications.length === 0"
                    >
                        <mat-icon>clear_all</mat-icon>
                    </button>
                </div>
            </div>

            <mat-divider></mat-divider>

            <!-- Pending invitations (Phase B.4) -->
            <div class="invitations-section" *ngIf="pendingInvitations.length > 0">
                <div class="section-header">
                    <mat-icon class="section-icon">mail</mat-icon>
                    <span
                        >You've been invited
                        <span class="section-count">({{ pendingInvitations.length }})</span>
                    </span>
                </div>
                <div class="invitation-card" *ngFor="let inv of pendingInvitations; trackBy: trackInvitation">
                    <div class="invitation-body">
                        <div class="invitation-title">
                            <strong>{{ inv.orgName }}</strong>
                            <span class="invitation-by" *ngIf="inv.inviterName"> · from {{ inv.inviterName }}</span>
                        </div>
                        <div class="invitation-meta" *ngIf="(inv.positions?.length || 0) + (inv.hats?.length || 0) > 0">
                            <ng-container *ngIf="inv.positions?.length">
                                <mat-icon class="meta-icon">badge</mat-icon>
                                <span
                                    >{{ inv.positions?.length }} position<span
                                        *ngIf="(inv.positions?.length || 0) !== 1"
                                        >s</span
                                    ></span
                                >
                            </ng-container>
                            <ng-container *ngIf="inv.hats?.length">
                                <mat-icon class="meta-icon">workspaces</mat-icon>
                                <span
                                    >{{ inv.hats?.length }} hat<span *ngIf="(inv.hats?.length || 0) !== 1"
                                        >s</span
                                    ></span
                                >
                            </ng-container>
                        </div>
                        <div class="invitation-actions">
                            <button
                                mat-stroked-button
                                color="warn"
                                [disabled]="busyInvitationKeys.has(inv.key)"
                                (click)="onDeclineInvitation(inv)"
                            >
                                Decline
                            </button>
                            <button
                                mat-flat-button
                                color="primary"
                                [disabled]="busyInvitationKeys.has(inv.key)"
                                (click)="onAcceptInvitation(inv)"
                            >
                                <mat-spinner *ngIf="busyInvitationKeys.has(inv.key)" diameter="16"></mat-spinner>
                                <span *ngIf="!busyInvitationKeys.has(inv.key)">Accept</span>
                            </button>
                        </div>
                    </div>
                </div>
                <mat-divider></mat-divider>
            </div>

            <!-- Loading -->
            <div *ngIf="state.loading" class="loading-container">
                <mat-spinner diameter="32"></mat-spinner>
            </div>

            <!-- Error State -->
            <div *ngIf="state.error && !state.loading" class="error-state">
                <mat-icon>error_outline</mat-icon>
                <p>{{ state.error }}</p>
                <button mat-button color="primary" (click)="retry()">Retry</button>
            </div>

            <!-- Notifications List -->
            <div class="notifications-list" *ngIf="!state.loading && !state.error">
                <ng-container *ngIf="state.notifications.length > 0; else emptyState">
                    <newgenus-notification-item
                        *ngFor="let notification of state.notifications; trackBy: trackByKey"
                        [notification]="notification"
                        (action)="onNotificationAction($event)"
                        (dismiss)="onDismiss($event)"
                    ></newgenus-notification-item>

                    <!-- Load More -->
                    <div class="load-more" *ngIf="state.hasMore">
                        <button mat-button (click)="loadMore()">Load More</button>
                    </div>
                </ng-container>

                <ng-template #emptyState>
                    <div class="empty-state">
                        <mat-icon>notifications_off</mat-icon>
                        <p>No notifications</p>
                        <span class="empty-subtext">You're all caught up!</span>
                    </div>
                </ng-template>
            </div>

            <mat-divider></mat-divider>

            <!-- Footer -->
            <div class="panel-footer">
                <button mat-button (click)="viewAll()">View All Notifications</button>
            </div>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .notification-panel {
                width: 100%;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                /* Inherit from mat-menu theming */
            }

            .panel-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 12px 16px;

                h3 {
                    margin: 0;
                    font-size: 16px;
                    font-weight: 500;
                }

                .header-actions {
                    display: flex;
                    gap: 4px;
                }
            }

            .notifications-list {
                max-height: 480px;
                overflow-y: auto;
                padding-right: 4px;

                /* Custom scrollbar styling - match policy dialog */
                &::-webkit-scrollbar {
                    width: 8px;
                }

                &::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 4px;
                }

                &::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 4px;

                    &:hover {
                        background: rgba(255, 255, 255, 0.3);
                    }
                }

                /* Firefox scrollbar */
                scrollbar-width: thin;
                scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
            }

            .loading-container {
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 32px;
                min-height: 120px;
            }

            .error-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 24px 16px;

                mat-icon {
                    font-size: 36px;
                    width: 36px;
                    height: 36px;
                    margin-bottom: 8px;
                    opacity: 0.7;
                }

                p {
                    margin: 0 0 12px 0;
                    opacity: 0.7;
                    font-size: 14px;
                }
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 32px 24px;

                mat-icon {
                    font-size: 40px;
                    width: 40px;
                    height: 40px;
                    margin-bottom: 12px;
                    opacity: 0.4;
                }

                p {
                    margin: 0;
                    font-size: 15px;
                    opacity: 0.7;
                }

                .empty-subtext {
                    margin-top: 4px;
                    font-size: 13px;
                    opacity: 0.5;
                }
            }

            .load-more {
                display: flex;
                justify-content: center;
                padding: 8px;
            }

            .panel-footer {
                display: flex;
                justify-content: center;
                padding: 8px;
            }

            /* Phase B.4 — pending invitation cards */
            .invitations-section {
                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 16px 4px;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    opacity: 0.75;

                    .section-icon {
                        font-size: 16px;
                        width: 16px;
                        height: 16px;
                    }
                    .section-count {
                        opacity: 0.6;
                        font-weight: 400;
                        margin-left: 2px;
                    }
                }
            }
            .invitation-card {
                padding: 8px 16px 12px;
                border-left: 3px solid #1976d2;
                background: rgba(25, 118, 210, 0.04);
                margin: 0 0 4px;

                .invitation-title {
                    font-size: 14px;
                    line-height: 1.3;
                    margin-bottom: 4px;
                }
                .invitation-by {
                    opacity: 0.7;
                    font-weight: 400;
                }
                .invitation-meta {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    opacity: 0.75;
                    margin-bottom: 8px;

                    .meta-icon {
                        font-size: 14px;
                        width: 14px;
                        height: 14px;
                    }
                }
                .invitation-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 8px;

                    button {
                        min-width: 88px;
                    }
                }
            }
        `,
    ],
})
export class NotificationPanelComponent implements OnInit, OnDestroy {
    @Output() closePanel = new EventEmitter<void>();

    state: NotificationState = {
        notifications: [],
        unreadCount: 0,
        loading: false,
        error: null,
        hasMore: false,
    };

    /** Phase B.4 — pending invitations addressed to the logged-in user. */
    pendingInvitations: InvitationDocument[] = [];
    /** Tracks in-flight Accept/Decline calls per invitation key. */
    busyInvitationKeys = new Set<string>();

    private readonly invitationService = inject(InvitationService);
    private readonly afAuth = inject(AngularFireAuth);
    private readonly snackBar = inject(MatSnackBar);

    private readonly destroy$ = new Subject<void>();

    constructor(
        private notificationService: NotificationService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.notificationService.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
            this.state = state;
        });

        // Phase B.4 — stream pending invitations addressed to current user.
        this.afAuth.authState
            .pipe(
                takeUntil(this.destroy$),
                switchMap((user) =>
                    user?.uid
                        ? this.invitationService.streamPendingInvitationsForUser(user.uid)
                        : of([] as InvitationDocument[])
                )
            )
            .subscribe((list) => {
                this.pendingInvitations = list;
            });
    }

    trackInvitation(_: number, inv: InvitationDocument): string {
        return inv.key;
    }

    async onAcceptInvitation(inv: InvitationDocument): Promise<void> {
        if (!inv?.token || this.busyInvitationKeys.has(inv.key)) return;
        this.busyInvitationKeys.add(inv.key);
        try {
            const res = await this.invitationService.acceptInvitation(inv.token).toPromise();
            if (res?.success) {
                this.snackBar.open(`Joined ${inv.orgName}.`, 'OK', { duration: 3000 });
            } else {
                this.snackBar.open(res?.message || 'Could not accept invitation.', 'OK', { duration: 4000 });
            }
        } catch (err: any) {
            console.error('Accept invitation error', err);
            this.snackBar.open(err?.error?.message || err?.message || 'Could not accept invitation.', 'OK', {
                duration: 4000,
            });
        } finally {
            this.busyInvitationKeys.delete(inv.key);
        }
    }

    async onDeclineInvitation(inv: InvitationDocument): Promise<void> {
        if (!inv?.token || this.busyInvitationKeys.has(inv.key)) return;
        this.busyInvitationKeys.add(inv.key);
        try {
            const res = await this.invitationService.declineInvitation(inv.token).toPromise();
            if (res?.success) {
                this.snackBar.open(`Invitation to ${inv.orgName} declined.`, 'OK', { duration: 3000 });
            } else {
                this.snackBar.open(res?.message || 'Could not decline invitation.', 'OK', { duration: 4000 });
            }
        } catch (err: any) {
            console.error('Decline invitation error', err);
            this.snackBar.open(err?.error?.message || err?.message || 'Could not decline invitation.', 'OK', {
                duration: 4000,
            });
        } finally {
            this.busyInvitationKeys.delete(inv.key);
        }
    }

    trackByKey(index: number, notification: InAppNotification): string {
        return notification.key;
    }

    async markAllRead(): Promise<void> {
        try {
            await this.notificationService.markAllAsRead();
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    }

    async dismissAll(): Promise<void> {
        try {
            await this.notificationService.dismissAll();
        } catch (error) {
            console.error('Error dismissing all:', error);
        }
    }

    async loadMore(): Promise<void> {
        await this.notificationService.loadMore();
    }

    retry(): void {
        // Re-initialize with the same user
        // This would need the current user ID - typically get from auth service
    }

    viewAll(): void {
        this.closePanel.emit();
        this.router.navigate(['/app/notifications']);
    }

    async onNotificationAction(notification: InAppNotification): Promise<void> {
        this.closePanel.emit();
        await this.notificationService.handleNotificationClick(notification);
    }

    async onDismiss(notification: InAppNotification): Promise<void> {
        try {
            await this.notificationService.dismiss(notification.key);
        } catch (error) {
            console.error('Error dismissing notification:', error);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
